/**
 * API COVERAGE — KYC and support access (Module 0, O12 / O13)
 *   GET    /api/kyc                     → kycApi.get
 *   PUT    /api/kyc/signatory           → kycApi.saveSignatory
 *   PUT    /api/kyc/owner-id            → kycApi.saveOwnerId
 *   POST   /api/kyc/files               → kycApi.upload (multipart)
 *   DELETE /api/kyc/files/{id}          → kycApi.remove
 *   GET    /api/kyc/files/{id}          → kycApi.open (blob)
 *   POST   /api/kyc/submit              → kycApi.submit
 *   GET/PUT /api/support-access         → supportAccessApi.*
 *   POST   /api/auth/support-login      → supportAccessApi.login
 */
import { api, http } from '@/lib/apiClient';
import type { AuthResult } from '@/types/auth';

export type KycStatus = 'NotStarted' | 'Draft' | 'Submitted' | 'Approved' | 'Rejected';
export type KycCheck = 'Waiting' | 'Matches' | 'MatchesGstRecord' | 'Mismatch' | 'Unreadable';
export type KycDocumentKind = 'OwnerId' | 'GstCertificate' | 'PanCard' | 'AuthorityToSign' | 'CancelledCheque';

export interface KycFile {
  id: number;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
  check: KycCheck;
  checkNote?: string | null;
}

export interface KycData {
  status: KycStatus;
  reviewNote?: string | null;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  canEdit: boolean;
  signatory: {
    name?: string | null;
    designation?: string | null;
    mobile?: string | null;
    email?: string | null;
    mobileVerified: boolean;
    emailVerified: boolean;
    isSoleOwner: boolean;
  };
  ownerId: { type?: 'Aadhaar' | 'Passport' | null; nameOnId?: string | null; numberMasked?: string | null; scanned: boolean; file?: KycFile | null };
  documents: { kind: KycDocumentKind; label: string; needed: string; required: boolean; file?: KycFile | null }[];
  missing: string[];
  message?: string | null;
}

export const kycApi = {
  get: () => api.get<KycData>('/kyc'),
  saveSignatory: (body: { name: string; designation: string; isSoleOwner: boolean }) => api.put<KycData>('/kyc/signatory', body),
  saveOwnerId: (body: { type: string; nameOnId: string; idNumber?: string | null }) => api.put<KycData>('/kyc/owner-id', body),
  upload: (kind: KycDocumentKind, file: File, ownerIdType?: string) => {
    const form = new FormData();
    form.append('kind', kind);
    if (ownerIdType) form.append('ownerIdType', ownerIdType);
    form.append('file', file);
    return api.post<KycData>('/kyc/files', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000 });
  },
  remove: (id: number) => api.del<KycData>(`/kyc/files/${id}`),
  submit: () => api.post<KycData>('/kyc/submit'),
  /** Opens a private KYC file in a new tab (fetched with the session, never by a public link). */
  open: (id: number) => openBlob(`/kyc/files/${id}`),
  /** The file's bytes, for the in-page document viewer. */
  blob: (id: number) => fetchBlob(`/kyc/files/${id}`),
};

/** A private file's bytes (fetched with the session), for the in-page document viewer. */
export async function fetchBlob(url: string): Promise<Blob> {
  const res = await http.get(url, { responseType: 'blob' });
  return res.data as Blob;
}

export async function openBlob(url: string) {
  // Open the tab first (inside the click), so the browser does not block it as a pop-up.
  const tab = window.open('', '_blank');
  const res = await http.get(url, { responseType: 'blob' });
  const href = URL.createObjectURL(res.data as Blob);
  if (tab) tab.location.href = href;
  else window.location.assign(href);
  window.setTimeout(() => URL.revokeObjectURL(href), 60_000);
}

export interface SupportAccess {
  active: boolean;
  until?: string | null;
  grantedBy?: string | null;
  recentSessions: { usedAt: string; by?: string | null }[];
}

export const supportAccessApi = {
  get: () => api.get<SupportAccess>('/support-access'),
  set: (hours: 0 | 24 | 72) => api.put<SupportAccess>('/support-access', { hours }),
  login: (code: string) => api.post<AuthResult>('/auth/support-login', { code }),
};
